import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  IconDown,
  IconLoader,
  IconTrash,
  IconUp,
  OnSelectChange,
  Select,
  SelectSize,
  Textarea,
} from '@ui/src';
import {
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  NewApplicationStatus,
} from '../../../../hoc/new-application/types';
import { AccountCurrencyTypes } from '../../types';
import InfoRow from '../../info-row';

import cn from '../../new-application.module.css';

const AccountCurrency = ({
  accountsCurrencies,
  accountCurrency,
  setAccountsCurrenciesValues,
  index,
  currencies,
  status,
  isEditable,
  onDeleteAccountCurrencies,
  selectedAccountsCurrencyId,
  deleteAccountsCurrenciesInProgress,
  setAccountsFlowsValues,
  accountsFlowsValues,
}: AccountCurrencyTypes) => {
  const [expanded, setExpanded] = useState(true);
  const [currencyId, setCurrencyId] = useState('');
  const [accountOpeningPurpose, setAccountOpeningPurpose] = useState('');
  const [plannedTransactionNature, setPlannedTransactionNature] = useState('');
  const [currencyError, setCurrencyError] = useState('');

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const nextValues = accountsCurrencies?.map((item, valueIndex) => {
      const result = { ...item };

      if (index === valueIndex) {
        if (e.target.name === `currencyId-${index}`) {
          setCurrencyError('');
          result.currencyId = e.target.value as string;
          setCurrencyId(e.target.value as string);

          if (
            accountsCurrencies?.find(
              (accountCurrencyValue) =>
                accountCurrencyValue.currencyId === e.target.value,
            )
          ) {
            setCurrencyError('Currency must be unique');
          } else {
            let nextAccountsFlows: AccountsFlowsFormValues[] = [];

            if (accountsFlowsValues) {
              nextAccountsFlows = ([] as AccountsFlowsFormValues[]).concat(
                accountsFlowsValues,
              );
            }

            if (
              currencyId &&
              e.target.value !== currencyId &&
              nextAccountsFlows?.find((flow) => flow.currencyId === currencyId)
            ) {
              nextAccountsFlows = nextAccountsFlows?.map((flow) => {
                const flowResult = { ...flow };

                flowResult.currencyId = e.target.value as string;

                return flowResult;
              });
            } else {
              const accountsFlowsBody: AccountsFlowsFormValues[] = [
                {
                  id: `accounts-flow-${uuid()}`,
                  transactionType: 'INCOMING',
                  currencyId: e.target.value as string,
                  expectedAnnualCreditTurnover: '',
                  maxTransferAmountPerTransaction: '',
                  transactionFrequency: '',
                  transactionFrequencyPeriodMonths: '',
                },
                {
                  id: `accounts-flow-${uuid()}`,
                  transactionType: 'OUTGOING',
                  currencyId: e.target.value as string,
                  expectedAnnualCreditTurnover: '',
                  maxTransferAmountPerTransaction: '',
                  transactionFrequency: '',
                  transactionFrequencyPeriodMonths: '',
                },
              ];

              nextAccountsFlows = [...nextAccountsFlows, ...accountsFlowsBody];
            }

            setAccountsFlowsValues(nextAccountsFlows);
          }
        } else if (e.target.name === `accountOpeningPurpose-${index}`) {
          result.accountOpeningPurpose = e.target.value as string;
          setAccountOpeningPurpose(e.target.value as string);
        } else if (e.target.name === `plannedTransactionNature-${index}`) {
          result.plannedTransactionNature = e.target.value as string;
          setPlannedTransactionNature(e.target.value as string);
        }
      }

      return result;
    });

    setAccountsCurrenciesValues(nextValues as AccountsCurrenciesFormValues[]);
  };

  useEffect(() => {
    if (accountCurrency) {
      setCurrencyId(accountCurrency.currencyId);
      setAccountOpeningPurpose(accountCurrency.accountOpeningPurpose);
      setPlannedTransactionNature(accountCurrency.plannedTransactionNature);
    }
  }, [accountCurrency]);

  return (
    <div
      className={`${cn.accountCurrencyWrapper} ${!expanded ? cn.collapsed : ''}`}
    >
      <div
        className={`${cn.accountCurrencyTopWrapper} ${!expanded ? cn.collapsed : ''}`}
      >
        <div className={cn.accountCurrencyTop}>
          <button
            type="button"
            className={cn.accountCurrencyTopLeft}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <IconUp width={14} height={14} />
            ) : (
              <IconDown height={14} width={14} />
            )}
            {currencyId
              ? currencies?.find((item) => item.value === currencyId)?.name
              : `Currency #${index + 1}`}
          </button>

          {isEditable &&
            status !== NewApplicationStatus.ACCEPTED &&
            status !== NewApplicationStatus.DECLINED && (
              <button
                type="button"
                className={cn.deleteAccountCurrencyButton}
                onClick={() =>
                  onDeleteAccountCurrencies(accountCurrency?.id as string)
                }
              >
                {deleteAccountsCurrenciesInProgress &&
                selectedAccountsCurrencyId === accountCurrency?.id ? (
                  <IconLoader width={18} height={18} />
                ) : (
                  <IconTrash />
                )}
              </button>
            )}
        </div>
      </div>
      {expanded && (
        <>
          <InfoRow title="Currency of the account" lessPadding>
            <Select
              name={`currencyId-${index}`}
              placeholder="Select currency..."
              list={currencies}
              value={currencyId}
              size={SelectSize.SMALL}
              onChange={onChange}
              selectWrapperClassName={
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.selectDisabled
                  : ''
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              error={currencyError}
              borderless
              fullWidth
            />
          </InfoRow>
          <InfoRow
            title="Purpose of the account opening"
            className={cn.noFlex}
            lessPadding
          >
            <Textarea
              name={`accountOpeningPurpose-${index}`}
              placeholder="Describe purpose..."
              value={accountOpeningPurpose}
              onChange={(e) => {
                onChange(e);
              }}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          </InfoRow>
          <InfoRow
            title="Nature of the planned transactions"
            className={cn.noFlex}
            lessPadding
          >
            <Textarea
              name={`plannedTransactionNature-${index}`}
              placeholder="Describe purpose..."
              value={plannedTransactionNature}
              onChange={(e) => {
                onChange(e);
              }}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          </InfoRow>
        </>
      )}
    </div>
  );
};

export default AccountCurrency;
